import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import CustomButton from "components/common/Button/CustomButton";
import InputField from "components/common/Input/Input";
import { toast } from "react-toastify";
import { FiCheck, FiCopy, FiPlus } from "react-icons/fi";
import StylePreservingDocumentEditor from "./StylePreservingDocumentEditor";

const PLACEHOLDER_LABELS = {
  org_logo: "Organisation logo",
  org_name: "Organisation name",
  org_email: "Organisation email",
  org_phone: "Organisation phone",
  businessname: "Business name",
  participant_name: "Participant name",
  PhoneNumber: "Participant phone",
  ContactNumber: "Participant alternate phone",
  pt_email: "Participant email",
  StartDate: "Agreement start date",
  validtill: "Agreement end date",
  agreementdate: "Agreement signed date",
  CancellationNotice: "Cancellation notice (days)",
  ESAP: "Ending agreement notice (weeks)",
  PlanManagedby: "Plan managed by",
  PaymentType: "Payment method",
  PMTerms: "Payment period",
  PlanManagerNAme: "Plan manager name",
  PMEmail: "Plan manager email",
  Consent: "Plan manager consent",
  table: "Schedule of supports (services table)",
  Image: "Participant / representative signature",
  Image2: "Authorised person signature",
  pt_signature_date: "Participant signature date",
  author_signature_date: "Authorised person signature date",
  representative_or_pt_name: "Representative or participant name",
  name_of_sign: "Participant signature label",
  author_sign_name: "Authorised person signature label",
  paragraphPlanManaged: "Payment / plan management wording",
};

const PDF_SIGNATURE_DATE_KEYS = {
  pt_signature_date: "participantrepresentative_signature_date",
  author_signature_date: "signature_date",
};

const DEFAULT_TRANSFORM_BY_TYPE = {
  date: "date:d/m/Y",
  table: "html_table",
  signature: "img_html",
  html: "raw",
};

const guessTransform = (field) => {
  if (!field) return "raw";
  if (field.type && DEFAULT_TRANSFORM_BY_TYPE[field.type]) {
    return DEFAULT_TRANSFORM_BY_TYPE[field.type];
  }
  const name = (field.name || "").toLowerCase();
  if (name.includes("date")) return "date:d/m/Y";
  if (name.includes("signature") || name.includes("image")) return "img_html";
  if (name === "services" || name.includes("table")) return "html_table";
  // Participant / organisation names: title-case for PDF display.
  if (
    name === "full_name" ||
    name === "first_name" ||
    name === "last_name" ||
    name === "participant_name" ||
    name === "org_name" ||
    name === "businessname" ||
    name === "name" // service_provider.name (org_name / businessname)
  ) {
    return "ucwords";
  }
  return "raw";
};

const friendlyLabel = (placeholder) =>
  PLACEHOLDER_LABELS[placeholder] ||
  String(placeholder)
    .replace(/^custom_/, "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Split a full HTML document into the parts before/after the editable body so
// we can keep the <head>/<style>/doctype wrapper intact while editing content.
const splitHtml = (html) => {
  const source = String(html || "");
  const openMatch = source.match(/<body[^>]*>/i);
  const closeIndex = source.toLowerCase().lastIndexOf("</body>");
  if (openMatch && closeIndex !== -1) {
    const openEnd = openMatch.index + openMatch[0].length;
    return {
      before: source.slice(0, openEnd),
      body: source.slice(openEnd, closeIndex),
      after: source.slice(closeIndex),
    };
  }
  return { before: "", body: source, after: "" };
};

const groupNameForSource = (source) => {
  switch (source) {
    case "participant":
      return "Participant profile";
    case "service_provider":
      return "Your organisation";
    case "signature":
      return "Signatures";
    case "computed":
      return "Auto-generated";
    default:
      return "Form answers";
  }
};

const PdfTemplateTab = ({
  template,
  starter,
  forms = [],
  selectedFormTemplateId,
  templateMode = "default",
  hasCustom = false,
  availableFields,
  saving,
  previewing,
  clearing,
  isUserGracePeriodOver,
  onFormChange,
  onSwitchMode,
  onSave,
  onPreview,
  onClear,
}) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [wrapperBefore, setWrapperBefore] = useState("");
  const [wrapperAfter, setWrapperAfter] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [fieldMappings, setFieldMappings] = useState({});
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [fieldSearch, setFieldSearch] = useState("");
  const [copiedPlaceholder, setCopiedPlaceholder] = useState("");
  const [detailCategory, setDetailCategory] = useState("all");
  const [quickVariable, setQuickVariable] = useState("");
  const editorApiRef = useRef(null);

  const selectedForm = useMemo(
    () =>
      forms.find((f) => Number(f.id) === Number(selectedFormTemplateId)) ||
      null,
    [forms, selectedFormTemplateId]
  );

  const applySource = (source) => {
    if (!source) return;
    const html =
      source.custom_html_content ||
      source.html_content ||
      source.default_html_content ||
      "";
    const { before, body, after } = splitHtml(html);
    setWrapperBefore(before);
    setWrapperAfter(after);
    setBodyHtml(body);
    setFieldMappings(source.field_mappings || {});
    setName(source.name || "NDIS Service Agreement");
  };

  useEffect(() => {
    if (template) {
      applySource(template);
      return;
    }
    if (starter) {
      applySource(starter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, starter]);

  const buildFullHtml = () => {
    if (!bodyHtml || !bodyHtml.trim()) return "";
    return `${wrapperBefore || ""}${bodyHtml}${wrapperAfter || ""}`;
  };

  const insertOptions = useMemo(() => {
    const fromForm = (availableFields?.form_fields || []).map((field) => ({
      label: field.label || field.name,
      group: field.section || "Form answers",
      category: "form",
      value: `form_field::${field.name}`,
      placeholder: field.suggested_placeholder || field.name,
      source: "form_field",
      key: field.name,
      type: field.type,
    }));
    const fromSystem = (availableFields?.system_fields || []).map((field) => {
      const category =
        field.category ||
        (field.source === "service_provider"
          ? "organisation"
          : field.source === "participant"
          ? "participant"
          : field.source === "signature"
          ? "signatures"
          : "auto");
      return {
        label: field.label || field.name,
        group: category === "signatures" ? "Signatures" : groupNameForSource(field.source),
        category,
        value: `${field.source}::${field.suggested_placeholder || field.name}`,
        placeholder: field.suggested_placeholder || field.name,
        source: field.source,
        key: field.name,
        type: field.type,
      };
    });
    return [...fromSystem, ...fromForm];
  }, [availableFields]);

  const generalVariables = useMemo(
    () =>
      insertOptions.filter((opt) =>
        ["organisation", "participant", "signatures", "auto"].includes(
          opt.category
        )
      ),
    [insertOptions]
  );

  const filteredInsertOptions = useMemo(() => {
    const q = fieldSearch.trim().toLowerCase();
    return insertOptions.filter((opt) => {
      if (detailCategory !== "all" && opt.category !== detailCategory) {
        return false;
      }
      if (!q) return true;
      const hay = `${opt.label} ${opt.group} ${opt.placeholder} ${opt.key}`.toLowerCase();
      return hay.includes(q);
    });
  }, [insertOptions, fieldSearch, detailCategory]);

  const groupedInsertOptions = useMemo(() => {
    const order = [
      "organisation",
      "participant",
      "form",
      "signatures",
      "auto",
    ];
    const labels = {
      organisation: "Organisation (general)",
      participant: "Participant profile",
      form: "Form answers",
      signatures: "Signatures",
      auto: "Auto-generated",
    };
    const buckets = {};
    filteredInsertOptions.forEach((opt) => {
      const key = opt.category || "form";
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(opt);
    });
    return order
      .filter((key) => buckets[key]?.length)
      .map((key) => ({
        key,
        title: labels[key] || key,
        items: buckets[key],
      }));
  }, [filteredInsertOptions]);

  const categoryFilters = [
    { id: "all", label: "All" },
    { id: "organisation", label: "Organisation" },
    { id: "participant", label: "Participant" },
    { id: "form", label: "Form" },
    { id: "signatures", label: "Signatures" },
    { id: "auto", label: "Auto" },
  ];

  const usedPlaceholders = useMemo(() => {
    const found = new Set();
    const regex = /\{\{\s*([\w.]+)\s*\}\}/g;
    let match;
    while ((match = regex.exec(bodyHtml || "")) !== null) {
      found.add(match[1]);
    }
    return Array.from(found);
  }, [bodyHtml]);

  const ensureMapping = (option) => {
    setFieldMappings((prev) => {
      if (prev[option.placeholder]) return prev;
      return {
        ...prev,
        [option.placeholder]: {
          source: option.source,
          key: option.key,
          transform: guessTransform({ name: option.key, type: option.type }),
        },
      };
    });
  };

  /**
   * Ensure every {{placeholder}} in the HTML has a field_mappings entry.
   * Also repairs known signature-date PDF tokens that were previously saved
   * with the wrong key (token name instead of the real form field).
   */
  const syncMappingsFromHtml = (html, mappings) => {
    const found = new Set();
    const regex = /\{\{\s*([\w.]+)\s*\}\}/g;
    let match;
    while ((match = regex.exec(html || "")) !== null) {
      found.add(match[1]);
    }

    const byPlaceholder = {};
    insertOptions.forEach((opt) => {
      byPlaceholder[opt.placeholder] = opt;
    });

    const next = { ...(mappings || {}) };
    found.forEach((ph) => {
      const option = byPlaceholder[ph];
      const knownDateKey = PDF_SIGNATURE_DATE_KEYS[ph];
      const existing = next[ph];

      if (existing) {
        const key = existing.key || "";
        const needsDateRepair =
          knownDateKey &&
          (key !== knownDateKey ||
            existing.occurrence != null ||
            existing.source !== "form_field");
        if (needsDateRepair) {
          const { occurrence, ...rest } = existing;
          next[ph] = {
            ...rest,
            source: "form_field",
            key: knownDateKey,
            transform: "date:d/m/Y",
          };
          return;
        }
        if (
          option &&
          option.category === "signatures" &&
          option.type === "date" &&
          (key === ph || key !== option.key)
        ) {
          next[ph] = {
            source: option.source,
            key: option.key,
            transform: "date:d/m/Y",
          };
        }
        return;
      }

      if (option) {
        next[ph] = {
          source: option.source,
          key: option.key,
          transform: guessTransform({ name: option.key, type: option.type }),
        };
        return;
      }

      if (knownDateKey) {
        next[ph] = {
          source: "form_field",
          key: knownDateKey,
          transform: "date:d/m/Y",
        };
        return;
      }

      // Fallback: treat token name as the form field / signature key.
      next[ph] = {
        source: "form_field",
        key: ph,
        transform: guessTransform({ name: ph }),
      };
    });
    return next;
  };

  const copyDetail = async (option) => {
    if (!option) return;
    const token = `{{${option.placeholder}}}`;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(token);
      } else {
        const ta = document.createElement("textarea");
        ta.value = token;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      ensureMapping(option);
      setCopiedPlaceholder(option.placeholder);
      setTimeout(() => {
        setCopiedPlaceholder((current) =>
          current === option.placeholder ? "" : current
        );
      }, 2000);
      toast.success(`Copied ${token} — paste it where you want in the document`);
    } catch (e) {
      toast.error("Could not copy. Please try again.");
    }
  };

  const insertDetail = (option) => {
    if (!option) return;
    if (!bodyHtml || !bodyHtml.trim()) {
      toast.error("Start with the recommended template, then insert details");
      return;
    }
    const token = `{{${option.placeholder}}}`;
    const api = editorApiRef.current;
    if (api?.insertHtmlAtCursor) {
      api.insertHtmlAtCursor(
        `<strong style="font-family: Roboto;">${token}</strong>`
      );
    } else {
      setBodyHtml((prev) => `${prev || ""}<p><strong>${token}</strong></p>`);
    }
    ensureMapping(option);
    toast.success(
      `Inserted “${friendlyLabel(
        option.placeholder
      )}”. It fills in automatically in the PDF.`
    );
  };

  const handleUseRecommended = () => {
    if (!starter) {
      toast.error("Recommended template is not available right now");
      return;
    }
    applySource(starter);
    toast.success("Recommended agreement template applied");
  };

  const handleSave = async () => {
    const full = buildFullHtml();
    if (!full.trim()) {
      toast.error("Please write your agreement first");
      return;
    }
    if (!selectedFormTemplateId) {
      toast.error("Please select a Service Agreement form first");
      return;
    }
    const syncedMappings = syncMappingsFromHtml(full, fieldMappings);
    if (syncedMappings !== fieldMappings) {
      setFieldMappings(syncedMappings);
    }
    await onSave(
      {
        sp_form_template_id: Number(selectedFormTemplateId),
        name: name || selectedForm?.title || "NDIS Service Agreement",
        html_content: full,
        custom_html_content: full,
        template_mode: "custom",
        field_mappings: syncedMappings,
      },
      Boolean(template?.sp_form_template_id)
    );
  };

  const handlePreview = async () => {
    const full = buildFullHtml();
    if (!full.trim()) {
      toast.error("Please write your agreement first");
      return;
    }
    const syncedMappings = syncMappingsFromHtml(full, fieldMappings);
    if (syncedMappings !== fieldMappings) {
      setFieldMappings(syncedMappings);
    }
    const result = await onPreview({
      sp_form_template_id: selectedFormTemplateId
        ? Number(selectedFormTemplateId)
        : undefined,
      name,
      html_content: full,
      field_mappings: syncedMappings,
    });
    if (result?.html) {
      setPreviewHtml(result.html);
      setShowPreview(true);
    }
  };

  const formTitle = availableFields?.form_title || selectedForm?.title;
  const hasContent = Boolean(bodyHtml && bodyHtml.trim());
  const hasForms = forms.length > 0;
  const isCustomMode = templateMode === "custom";

  const formOptionSuffix = (form) => {
    if (form.template_mode === "custom") return " (Custom)";
    return " (Default)";
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-md p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Form to configure
          </label>
          {hasForms ? (
            <select
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              value={selectedFormTemplateId || ""}
              onChange={(e) => {
                const next = e.target.value ? Number(e.target.value) : null;
                if (onFormChange) onFormChange(next);
              }}
              disabled={isUserGracePeriodOver}
            >
              {forms.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.title}
                  {formOptionSuffix(form)}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
              No Service Agreement form templates were found for your
              organisation. Publish an NDIS Service Agreement form first.
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Each form has its own PDF settings. On download, settings are matched
            by form id.
          </p>
        </div>

        <InputField
          label="Agreement title"
          type="text"
          value={name}
          handleChange={setName}
          placeHolder="e.g. NDIS Service Agreement"
        />
        {formTitle ? (
          <p className="text-sm text-gray-600">
            Editing fields from:{" "}
            <span className="font-medium text-gray-900">{formTitle}</span>
            {hasCustom && !isCustomMode ? (
              <span className="ml-2 inline-flex items-center rounded-full bg-gray-50 text-gray-600 border border-gray-200 px-2 py-0.5 text-xs">
                Custom draft saved
              </span>
            ) : null}
          </p>
        ) : null}
      </div>

      {!hasContent ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-md p-8 text-center">
          <p className="text-sm text-gray-600 mb-3">
            No document yet. Start from the recommended agreement — then edit the
            wording, format text (bold, italic, colour) and insert participant
            details. Existing styling is preserved.
          </p>
          <CustomButton
            label="Use recommended template"
            variant="primary"
            clickHandler={handleUseRecommended}
            isDisabled={!starter || isUserGracePeriodOver}
          />
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden flex flex-col xl:h-[calc(100vh-13rem)] xl:min-h-[560px]">
          <div className="shrink-0 px-4 py-3 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Edit agreement document
              </h3>
              <p className="mt-0.5 text-xs text-gray-600">
                Scroll only inside the document area. The page will not jump when
                you reach the end.
              </p>
            </div>
            {usedPlaceholders.length ? (
              <p className="text-[11px] text-gray-500">
                {usedPlaceholders.length} auto-fill detail
                {usedPlaceholders.length === 1 ? "" : "s"} in document
              </p>
            ) : null}
          </div>

          <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-12">
            <div className="xl:col-span-8 min-h-0 h-[55vh] xl:h-full border-b xl:border-b-0 xl:border-r border-gray-200">
              <StylePreservingDocumentEditor
                html={bodyHtml}
                onChange={setBodyHtml}
                readOnly={isUserGracePeriodOver}
                fillHeight
                onReady={(api) => {
                  editorApiRef.current = api;
                }}
              />
            </div>

            <aside className="xl:col-span-4 min-h-0 h-[40vh] xl:h-full flex flex-col bg-gray-50">
              <div className="shrink-0 px-2.5 py-2 border-b border-gray-200 bg-white space-y-1.5">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">
                    Insert details
                  </h4>
                  <p className="text-[11px] text-gray-500 leading-snug">
                    Select a variable, then use copy or insert.
                  </p>
                </div>

                <div className="flex items-center gap-1">
                  <select
                    className="flex-1 min-w-0 rounded border border-gray-300 px-2 py-1.5 text-xs focus:border-indigo-500 focus:ring-indigo-500"
                    value={quickVariable}
                    onChange={(e) => setQuickVariable(e.target.value)}
                    disabled={isUserGracePeriodOver}
                    aria-label="General variables"
                    title="General variables"
                  >
                    <option value="">General variables…</option>
                    {generalVariables.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    title="Copy selected variable"
                    aria-label="Copy selected variable"
                    className="shrink-0 h-8 w-8 inline-flex items-center justify-center rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    disabled={isUserGracePeriodOver || !quickVariable}
                    onClick={() => {
                      const opt = generalVariables.find(
                        (o) => o.value === quickVariable
                      );
                      if (opt) copyDetail(opt);
                    }}
                  >
                    <FiCopy className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    title="Insert selected variable at cursor"
                    aria-label="Insert selected variable at cursor"
                    className="shrink-0 h-8 w-8 inline-flex items-center justify-center rounded bg-blue-dark text-white hover:opacity-90 disabled:opacity-40"
                    disabled={isUserGracePeriodOver || !quickVariable}
                    onClick={() => {
                      const opt = generalVariables.find(
                        (o) => o.value === quickVariable
                      );
                      if (opt) insertDetail(opt);
                    }}
                  >
                    <FiPlus className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex flex-wrap gap-0.5">
                  {categoryFilters.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setDetailCategory(cat.id)}
                      className={`text-[10px] px-1.5 py-0.5 rounded border ${
                        detailCategory === cat.id
                          ? "bg-blue-dark text-white border-blue-dark"
                          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>

                <input
                  type="search"
                  value={fieldSearch}
                  onChange={(e) => setFieldSearch(e.target.value)}
                  placeholder="Search…"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              <div
                className="flex-1 min-h-0 overflow-y-auto px-1.5 py-1.5 space-y-2"
                style={{ overscrollBehavior: "contain" }}
              >
                {groupedInsertOptions.length === 0 ? (
                  <p className="text-xs text-gray-500 py-4 px-2 text-center">
                    {fieldSearch || detailCategory !== "all"
                      ? "No matching details."
                      : "No details available."}
                  </p>
                ) : (
                  groupedInsertOptions.map((group) => (
                    <div key={group.key}>
                      <p className="sticky top-0 z-[1] bg-gray-50 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                        {group.title}
                      </p>
                      <ul className="divide-y divide-gray-100 rounded border border-gray-200 bg-white overflow-hidden">
                        {group.items.map((option) => {
                          const token = `{{${option.placeholder}}}`;
                          const justCopied =
                            copiedPlaceholder === option.placeholder;
                          return (
                            <li
                              key={option.value}
                              className="flex items-center gap-1.5 px-2 py-1.5 hover:bg-gray-50"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-gray-900 truncate">
                                  {option.label}
                                </p>
                                <code className="block text-[10px] font-mono text-gray-400 truncate">
                                  {token}
                                </code>
                              </div>
                              <button
                                type="button"
                                title={justCopied ? "Copied" : "Copy"}
                                aria-label={
                                  justCopied
                                    ? `Copied ${option.label}`
                                    : `Copy ${option.label}`
                                }
                                className={`shrink-0 h-7 w-7 inline-flex items-center justify-center rounded border ${
                                  justCopied
                                    ? "border-teal-300 bg-teal-50 text-teal-700"
                                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-100"
                                } disabled:opacity-40`}
                                onClick={() => copyDetail(option)}
                                disabled={isUserGracePeriodOver}
                              >
                                {justCopied ? (
                                  <FiCheck className="h-3.5 w-3.5" />
                                ) : (
                                  <FiCopy className="h-3.5 w-3.5" />
                                )}
                              </button>
                              <button
                                type="button"
                                title="Insert at cursor"
                                aria-label={`Insert ${option.label}`}
                                className="shrink-0 h-7 w-7 inline-flex items-center justify-center rounded bg-blue-dark text-white hover:opacity-90 disabled:opacity-40"
                                onClick={() => insertDetail(option)}
                                disabled={isUserGracePeriodOver}
                              >
                                <FiPlus className="h-3.5 w-3.5" />
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))
                )}
              </div>
            </aside>
          </div>

          <div className="shrink-0 px-4 py-3 border-t border-gray-200 bg-white flex flex-wrap gap-3 justify-between items-center">
            <div className="flex flex-wrap gap-3">
              {isCustomMode ? (
                <CustomButton
                  label="Use default PDF"
                  variant="secondary"
                  showLoading={clearing}
                  isDisabled={clearing || isUserGracePeriodOver}
                  clickHandler={onClear}
                />
              ) : null}
              <CustomButton
                label="Reset to recommended"
                variant="secondary"
                clickHandler={handleUseRecommended}
                isDisabled={!starter || isUserGracePeriodOver}
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <CustomButton
                label="Cancel"
                variant="secondary"
                clickHandler={() => navigate("/settings")}
              />
              <CustomButton
                label="Preview"
                variant="secondary"
                showLoading={previewing}
                isDisabled={previewing || !hasContent || isUserGracePeriodOver}
                clickHandler={handlePreview}
              />
              <CustomButton
                label="Save as custom PDF"
                variant="primary"
                showLoading={saving}
                isDisabled={
                  saving ||
                  !hasContent ||
                  !selectedFormTemplateId ||
                  isUserGracePeriodOver
                }
                clickHandler={handleSave}
              />
            </div>
          </div>
        </div>
      )}

      {showPreview && previewHtml ? (
        <div className="border border-gray-200 rounded-md p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900">
              Document preview
            </h4>
            <button
              type="button"
              className="text-sm text-gray-600 hover:underline"
              onClick={() => setShowPreview(false)}
            >
              Hide preview
            </button>
          </div>
          <div
            className="prose max-w-none overflow-auto max-h-[520px] border border-gray-100 rounded p-3 bg-white"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default PdfTemplateTab;
